import styled from 'styled-components';
import React, { useState, useEffect } from "react";
import { Row, Col } from "reactstrap";
import { OutputRow, TableIcon, BoldLabel, Colors } from "../../../../utils/budget-tool-specific";
import { RebelColors } from "../../../../styles/rebel-experiences";
import SubtotalColumns from "../../experiences/cost-summary/subtotal-cols";
import { displayValue } from "../../helper";
import { calculateSubCostNoFlightsPerAttendee, calculateCostPerAttendee, calculateInternalCostA, calculateInternalCostB, calculateRebelTotalCost } from "../../experiences/cost-summary/cost-helper";
import { GetDB } from "../../../../utils/database/firebase-budget-tool";
import { listenToDocument } from "../../../../utils/firestore-functions";

const RebelFinalCostRow = ({guideData, expData, clientCurrency, currencyConversionRatio, villaId}) => {

    const budgetDB = GetDB();
    const [villaData, setVillaData] = useState(null);

    useEffect(() => {
        if (villaId && villaId !== '0' && villaId !== '') {
            const unsubscribe = listenToDocument(budgetDB, "rebelVillas", villaId, setVillaData);
            return unsubscribe;
        }
    }, [villaId]);

    useEffect(() => {
        console.log('Villa notes:', villaData?.notes);
        console.log('Experience notes:', expData?.notes);
    }, [villaData, expData]);

    // Calculate margin cost
    const calculateMarginCost = () => {
        if (guideData.marginPercent === undefined || guideData.marginPercent.value === true) {
            // Percentage-based margin (existing calculation)
            return calculateInternalCostB(guideData, expData, clientCurrency, currencyConversionRatio);
        } else {
            // Dollar-based margin - per person, multiplied by attendees
            const attendeesCount = parseFloat(guideData.attendees?.value) || 1;
            return parseFloat(guideData.margin.value) * attendeesCount;
        }
    };

    const marginCost = calculateMarginCost();
    const overheadCost = calculateInternalCostA(guideData, expData, clientCurrency, currencyConversionRatio);
    const attendees = parseFloat(guideData.attendees?.value) || 1;
    const rebelSubtotal = calculateRebelTotalCost(guideData, expData);

    return (
        <React.Fragment>
            <SeparatorDiv/>
            <OutputRow>
            <Col style={{display: "flex", justifyContent: "flex-end"}}>
                    <RebelText style={{marginBottom: "16px"}}><b>Subtotal ({guideData.currency.value})</b></RebelText>
                </Col>
                <Col lg="1"/>
                <Col lg="2">
                    <RebelText style={{marginBottom: "16px"}}>{guideData.currency.value.slice(3)}{displayValue(rebelSubtotal)}</RebelText>
                </Col>
            </OutputRow>
            <OutputRow>
                <Col style={{display: "flex", justifyContent: "flex-end"}}>
                    {(guideData.overheadPercent === undefined || guideData.overheadPercent.value === true) ?
                        <RebelText style={{marginBottom: "16px"}}>Administrative & Management Fee ({guideData.overhead.value}%)</RebelText> :
                        <RebelText style={{marginBottom: "16px"}}>{(guideData.overheadPercent === undefined || guideData.margin.value === "0") ? "Administrative & Management Fee" : "Administrative & Management Fee"}</RebelText>
                    }
                </Col>
                <Col lg="1"/>
                <Col lg="2">
                    <RebelText style={{marginBottom: "16px"}}>{guideData.currency.value.slice(3)}{displayValue(overheadCost)}</RebelText>
                </Col>
            </OutputRow>
            {guideData.margin.value !== "0" && <OutputRow>
                <Col style={{display: "flex", justifyContent: "flex-end"}}>
                    {(guideData.marginPercent === undefined || guideData.marginPercent.value === true) ?
                        <RebelText style={{marginBottom: "16px"}}>Planning Fee ({guideData.margin.value}%) </RebelText> :
                        <RebelText style={{marginBottom: "16px"}}>Planning Fee ({guideData.currency.value.slice(3)}{displayValue(parseFloat(guideData.margin.value))}/pp x {attendees})</RebelText>
                    }
                </Col>
                <Col lg="1"/>
                <Col lg="2">
                    <RebelText style={{marginBottom: "16px"}}>{guideData.currency.value.slice(3)}{displayValue(marginCost)}</RebelText>
                </Col>
            </OutputRow>}
            <OutputRow>
                <Col style={{display: "flex", justifyContent: "flex-end"}}>
                    <RebelBoldLabel>Total Program Cost ({guideData.currency.value})</RebelBoldLabel>
                </Col>
                <Col lg="1"/>
                <Col lg="2">
                    <RebelBoldLabel>{guideData.currency.value.slice(3)}{displayValue(overheadCost + marginCost + rebelSubtotal)}</RebelBoldLabel>
                </Col>
            </OutputRow>
            <RebelNoBorderRow>
                <Col style={{display: "flex", justifyContent: "flex-end"}}>
                    <RebelHighlightedText>Total Cost Per Attendee ({guideData.currency.value})</RebelHighlightedText>
                </Col>
                <Col lg="1"/>
                <Col lg="2">
                    <RebelHighlightedText>{guideData.currency.value.slice(3)}{displayValue((overheadCost + marginCost + rebelSubtotal) / attendees)}</RebelHighlightedText>
                </Col>
            </RebelNoBorderRow>
            {(villaData?.notes || expData?.notes) && (
                <RebelNoBorderRow>
                    <Col>
                        {villaData?.notes && (
                            <NotesContainer>
                                <NotesTitle>Property Notes</NotesTitle>
                                <NotesContent>{villaData.notes}</NotesContent>
                            </NotesContainer>
                        )}
                        {expData?.notes && (
                            <NotesContainer>
                                <NotesTitle>Experience Notes</NotesTitle>
                                <NotesContent>{expData.notes}</NotesContent>
                            </NotesContainer>
                        )}
                    </Col>
                </RebelNoBorderRow>
            )}
        </React.Fragment>
    )
}

export default RebelFinalCostRow;

const SeparatorDiv = styled.div`
    background-color: ${RebelColors.gold};
    width: 100%;
    height: 5px;
    border-radius: 5px;
    margin-top: 5px;
    margin-bottom: 5px;
`;

const RebelText = styled.p`
    color: ${RebelColors.text};
`;

const RebelBoldLabel = styled(BoldLabel)`
    color: ${RebelColors.gold};
`;

const RebelHighlightedText = styled.p`
    color: ${RebelColors.gold};
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 0;
`;

const RebelNoBorderRow = styled(OutputRow).attrs({
    className: 'rebel-no-border'
})`
    && {
        border-bottom: none !important;
        align-items: center;
        min-height: auto;
        padding-top: 8px;
        padding-bottom: 0;
    }
`;

const HighlightedBoxContainer = styled.div`
    position: absolute;
    left: 15px;
    right: 15px;
    top: 0;
    border: 2px solid ${RebelColors.gold};
    border-radius: 6px;
    padding: 8px 0;
    display: flex;
    align-items: center;
    pointer-events: none;

    > * {
        pointer-events: auto;
    }
`;

const NotesContainer = styled.div`
    background: linear-gradient(135deg, rgba(253, 181, 6, 0.1) 0%, rgba(253, 181, 6, 0.05) 50%, transparent 100%);
    border: 1px solid rgba(253, 181, 6, 0.2);
    border-radius: 12px;
    padding: 20px;
    margin: 8px 0 0 0;
`;

const NotesTitle = styled.h4`
    color: ${RebelColors.gold};
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 12px;
`;

const NotesContent = styled.p`
    color: ${RebelColors.text};
    font-size: 14px;
    line-height: 1.6;
    margin: 0;
    white-space: pre-wrap;
`;
